import { useRef, useState } from 'react'
import { useApolloClient, useMutation } from '@apollo/client'
import { RefreshCw } from 'lucide-react'
import { Button as ButtonComponent } from '@/components/ui/button'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import HelperDialog, { HelperDialogLoading } from '@/components/campaigns/HelperDialog'
import LicenseOverviewForm, { LicenseOverview } from '@/components/campaigns/LicenseOverviewForm'
import SystemPromptEditor, {
    CustomSystemPromptBadge,
    EditSystemPromptButton,
} from '@/components/campaigns/SystemPromptEditor'
import GenerationBasisPanel from '@/components/generationBasis/GenerationBasisPanel'
import GenerationBasisSummary from '@/components/generationBasis/GenerationBasisSummary'
import { basisRequestOf, isBasisActive } from '@/components/generationBasis/basisUtils'
import { useGenerationBasis } from '@/components/generationBasis/useGenerationBasis'
import type { GenerationBasisView } from '@/components/generationBasis/types'
import { GET_LICENSE_OVERVIEW, SUGGEST_MEDIA_PROMPTS } from '@/graphql/ads/prompt-suggestion-queries'
import { SystemPromptChange, useSuggestionSystemPrompt } from '@/hooks/useSuggestionSystemPrompt'
import { ButtonProps } from '@/types'

// Type cast for the Button component from JSX file
const Button = ButtonComponent as React.FC<
    ButtonProps & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'aria-label'>
>

interface PromptSuggestion {
    title: string
    prompt: string
    persona?: string | null
    /** What was supplied to the suggestions; the same for each suggestion of a request */
    basis?: GenerationBasisView | null
}

interface SuggestMediaPromptsData {
    suggestMediaPrompts: PromptSuggestion[]
}

interface LicenseOverviewData {
    licenseOverview: LicenseOverview
}

interface PromptHelperProps {
    mediaType: 'IMAGE' | 'VIDEO'
    draft?: string
    onSelect: (prompt: string) => void
    disabled?: boolean
    className?: string
    /** Shown on the left of the trigger in the same row, such as the screen's Generate button */
    leading?: React.ReactNode
}

// Both are fixed in the overview form: the logo is uploaded there
const OVERVIEW_FORM_CODES = ['LICENSE_OVERVIEW_REQUIRED', 'LICENSE_LOGO_REQUIRED']
const OVERVIEW_LOAD_FAILED_MESSAGE = 'We could not load the license overview. Please close this dialog and try again.'

type OverviewStatus = 'idle' | 'loading' | 'ready' | 'failed'

const errorCodeOf = (error: unknown): string | null => {
    const graphQLErrors = (error as { graphQLErrors?: { extensions?: { code?: string } }[] })?.graphQLErrors
    return graphQLErrors?.[0]?.extensions?.code ?? null
}

/**
 * "Help me with the prompt" for AI image and video generation: asks the backend for 2–3 prompts grounded in the
 * license's website, logo, brand files, overview and personas (honouring the typed draft as requirements when there is
 * one) and hands the chosen prompt to `onSelect`. Each click opens a dialog with the license overview form, prefilled
 * with the saved data, so it can be checked or updated; nothing is suggested until it is submitted. "More ideas" asks
 * again without the form, "Edit overview" reopens it, and "Edit system prompt" shows the instructions the prompts are
 * written with, to edit, save on the license and regenerate. Choosing a prompt or closing the dialog ends the flow, so
 * the next click starts again from the form.
 */
const PromptHelper = ({ mediaType, draft, onSelect, disabled = false, className, leading }: PromptHelperProps) => {
    const { campaignId } = useWizard()
    const client = useApolloClient()
    const [dialogOpen, setDialogOpen] = useState(false)
    const [overview, setOverview] = useState<LicenseOverview | null>(null)
    const [overviewStatus, setOverviewStatus] = useState<OverviewStatus>('idle')
    const [formOpen, setFormOpen] = useState(false)
    const [suggestions, setSuggestions] = useState<PromptSuggestion[]>([])
    const [failed, setFailed] = useState(false)
    const [empty, setEmpty] = useState(false)
    const [suggest, { loading }] = useMutation<SuggestMediaPromptsData>(SUGGEST_MEDIA_PROMPTS)
    const kind = mediaType === 'VIDEO' ? 'VIDEO_PROMPTS' : 'IMAGE_PROMPTS'
    const systemPrompt = useSuggestionSystemPrompt({ campaignId, kind, active: dialogOpen })
    const basis = useGenerationBasis({ campaignId, purpose: 'CREATIVE_PROMPTS', active: dialogOpen })
    const [basisNotReady, setBasisNotReady] = useState(false)
    const draftText = draft?.trim() || null

    const requestInFlight = useRef(false)

    const requestSuggestions = async () => {
        // A second click while the insight is being read again must not send a second request
        if (requestInFlight.current) return
        requestInFlight.current = true
        try {
            await runSuggestions()
        } finally {
            requestInFlight.current = false
        }
    }

    const runSuggestions = async () => {
        setFailed(false)
        setEmpty(false)
        setBasisNotReady(false)
        basis.clearMessage()
        // Never send while the audience insight is being read or changed: its hash would be one the user has not seen.
        // When it turns out that no insight is switched on there is nothing to review, so the request goes ahead.
        let request = basis.request
        if (basis.blocked) {
            const current = basis.changing ? null : await basis.read()
            if (!current || isBasisActive(current)) {
                setSuggestions([])
                setBasisNotReady(true)
                return
            }
            request = basisRequestOf(current, basis.skip)
        }
        try {
            const { data } = await suggest({
                variables: { campaignId, mediaType, draft: draftText, ...(request ? { basis: request } : {}) },
            })
            const received = data?.suggestMediaPrompts ?? []
            setSuggestions(received)
            setEmpty(received.length === 0)
        } catch (error) {
            setSuggestions([])
            if (OVERVIEW_FORM_CODES.includes(errorCodeOf(error) ?? '')) setFormOpen(true)
            else if (basis.handleGenerationError(error)) setBasisNotReady(true)
            else setFailed(true)
        }
    }

    /**
     * Reloads the saved overview for the overview form. The form waits for it: opening it on stale or empty values
     * would make a save drop the brand files and logo the form never saw.
     */
    const loadOverview = async () => {
        setOverviewStatus('loading')
        try {
            const { data } = await client.query<LicenseOverviewData>({
                query: GET_LICENSE_OVERVIEW,
                variables: { campaignId },
                fetchPolicy: 'network-only',
            })
            setOverview(data.licenseOverview)
            setOverviewStatus('ready')
        } catch {
            setOverviewStatus('failed')
        }
    }

    /** Opens the dialog on the overview form; suggestions are only requested once the form is submitted. */
    const handleHelp = () => {
        setDialogOpen(true)
        setFormOpen(true)
        setSuggestions([])
        setFailed(false)
        setEmpty(false)
        setBasisNotReady(false)
        void loadOverview()
    }

    const openOverviewForm = () => {
        setBasisNotReady(false)
        setSuggestions([])
        setFormOpen(true)
    }

    /** Closes the dialog and clears the flow, so the next click starts again from the overview form. */
    const close = () => {
        setDialogOpen(false)
        setFormOpen(false)
        setSuggestions([])
        setFailed(false)
        setEmpty(false)
        setBasisNotReady(false)
        systemPrompt.reset()
        basis.reset()
    }

    const handleOverviewSaved = (saved: LicenseOverview) => {
        setOverview(saved)
        setFormOpen(false)
        requestSuggestions()
    }

    const handleSystemPromptApplied = (change: SystemPromptChange) => {
        systemPrompt.apply(change)
        requestSuggestions()
    }

    const handleSelect = (prompt: string) => {
        onSelect(prompt)
        close()
    }

    const busy = loading

    return (
        <div className={className}>
            <div className="tw:mt-4 tw:mb-2 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3">
                {leading}
                <Button
                    type="button"
                    size="sm"
                    className="tw:!ml-auto"
                    onClick={handleHelp}
                    disabled={disabled || busy || !campaignId}
                >
                    Help me with the prompt
                </Button>
            </div>

            <HelperDialog open={dialogOpen} onOpenChange={(open) => !open && close()} title="Help me with the prompt">
                {formOpen && campaignId && overviewStatus === 'loading' ? (
                    <HelperDialogLoading label="Loading the license overview…" />
                ) : formOpen && campaignId && overviewStatus === 'failed' ? (
                    <p role="alert" className="tw:text-sm tw:text-red-400 tw:m-0">
                        {OVERVIEW_LOAD_FAILED_MESSAGE}
                    </p>
                ) : formOpen && campaignId ? (
                    <>
                        <GenerationBasisPanel
                            basis={basis}
                            title="Audience insight for the prompts"
                            className="tw:mb-3"
                        />
                        <LicenseOverviewForm
                            bordered={false}
                            campaignId={campaignId}
                            initialValues={overview}
                            submitLabel="Suggest prompts"
                            onSaved={handleOverviewSaved}
                            onCancel={close}
                        />
                    </>
                ) : systemPrompt.editing && campaignId ? (
                    <SystemPromptEditor
                        kind={kind}
                        target={{ campaignId }}
                        draft={draftText}
                        onCancel={systemPrompt.cancel}
                        onApplied={handleSystemPromptApplied}
                    />
                ) : loading ? (
                    <HelperDialogLoading label="Generating suggestions…" />
                ) : (
                    <>
                        <GenerationBasisPanel
                            basis={basis}
                            title="Audience insight for the prompts"
                            className="tw:mb-3"
                        />

                        {basisNotReady && (
                            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:mb-2">
                                <p role="status" className="tw:text-sm tw:text-gray-400 tw:m-0">
                                    The prompts were not generated because the audience insight needs your attention
                                    first.
                                </p>
                                <Button type="button" variant="secondary" size="sm" onClick={requestSuggestions}>
                                    Suggest prompts
                                </Button>
                            </div>
                        )}

                        {failed && (
                            <p role="alert" className="tw:text-sm tw:text-red-400 tw:m-0">
                                We could not suggest prompts right now. Please try again.
                            </p>
                        )}

                        {empty && (
                            <p className="tw:text-sm tw:text-gray-400 tw:m-0">
                                There are no suggestions right now. Please try again.
                            </p>
                        )}

                        {(failed || empty) && (
                            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:mt-2">
                                {systemPrompt.custom && <CustomSystemPromptBadge />}
                                <Button type="button" variant="ghost" size="sm" onClick={openOverviewForm}>
                                    Edit overview
                                </Button>
                                {systemPrompt.custom && <EditSystemPromptButton onClick={systemPrompt.edit} />}
                            </div>
                        )}

                        {suggestions.length > 0 && (
                            <div>
                                <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2 tw:mb-2">
                                    <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                                        <span className="tw:text-sm tw:font-semibold">Pick a prompt to start from</span>
                                        {systemPrompt.custom && <CustomSystemPromptBadge />}
                                    </div>
                                    <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1">
                                        <Button type="button" variant="ghost" size="sm" onClick={openOverviewForm}>
                                            Edit overview
                                        </Button>
                                        <EditSystemPromptButton onClick={systemPrompt.edit} />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={requestSuggestions}
                                            disabled={loading || basis.blocked}
                                        >
                                            <RefreshCw size={14} />
                                            More ideas
                                        </Button>
                                    </div>
                                </div>

                                {(overview?.primaryUrl || Boolean(overview?.socialUrls?.length)) &&
                                    overview?.brandProfileStatus === 'failed' && (
                                        <p className="tw:text-xs tw:text-gray-400 tw:mt-0 tw:mb-2">
                                            We could not read the link, so these ideas use the rest of the license
                                            details.
                                        </p>
                                    )}

                                <GenerationBasisSummary basis={suggestions[0]?.basis} className="tw:mb-2" />

                                <ul
                                    aria-label="Prompt suggestions"
                                    className="tw:flex tw:flex-col tw:gap-2 tw:list-none tw:!p-0 tw:!m-0"
                                >
                                    {suggestions.map(({ title, prompt, persona }) => (
                                        <li key={prompt}>
                                            <button
                                                type="button"
                                                aria-label={`Use prompt: ${title}`}
                                                onClick={() => handleSelect(prompt)}
                                                className="tw:w-full tw:text-left tw:rounded-md tw:border tw:border-gray-600 tw:bg-transparent tw:p-3 tw:hover:border-blue-400 tw:focus-visible:border-blue-400 tw:transition-colors"
                                            >
                                                <span className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                                                    <span className="tw:text-sm tw:font-semibold tw:text-foreground">
                                                        {title}
                                                    </span>
                                                    {persona && (
                                                        <span className="tw:rounded-md tw:bg-[#334155] tw:px-2 tw:py-0.5 tw:text-xs tw:text-[#94A3B8]">
                                                            For: {persona}
                                                        </span>
                                                    )}
                                                </span>
                                                <span className="tw:block tw:text-sm tw:text-gray-300 tw:mt-1 tw:whitespace-pre-line">
                                                    {prompt}
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </>
                )}
            </HelperDialog>
        </div>
    )
}

export default PromptHelper
